/**
 * Typed widget configuration (public, build-time only — NO secrets ever live in
 * the widget). Per coding-standards.md ("Env via config objects"), feature code
 * (apiClient/hooks/components/launcher) must read configuration through this
 * module and never touch `process.env` directly.
 *
 * `WIDGET_API_BASE` is the RAG server's public API prefix. It defaults to
 * `/support-bot/api` (development-workflow.md#Environment Configuration); the
 * tsup production build (Story 4.4, `tsup.config.ts`) injects the per-deployment
 * value via `define`, so this module holds only the default + a resolver and
 * does not read `process.env` at widget runtime (there is none inside the iframe
 * bundle).
 */

import { deriveOrigin } from './messages.js';

/** Default public API base for the RAG server. */
export const DEFAULT_WIDGET_API_BASE = '/support-bot/api';

export interface WidgetConfig {
  /** Public base URL/prefix the apiClient posts to (e.g. `/support-bot/api`). */
  apiBase: string;
}

/**
 * Resolve the effective widget config from optional (host-provided /
 * build-injected) overrides, falling back to the safe defaults. Returns a
 * frozen object so config cannot be mutated at runtime (mirrors the server's
 * frozen config contract).
 */
export function resolveWidgetConfig(overrides?: Partial<WidgetConfig>): Readonly<WidgetConfig> {
  return Object.freeze({
    apiBase: overrides?.apiBase ?? DEFAULT_WIDGET_API_BASE,
  });
}

/** Default panel placement when the host does not specify one. */
export const DEFAULT_LAUNCHER_PLACEMENT = 'right' as const;
/** Default stacking context for the launcher + iframe panel. */
export const DEFAULT_LAUNCHER_Z_INDEX = 2147483000;

/**
 * Host-injected launcher configuration (Story 4.4). This is the shape a host
 * surface passes to `<AskLauncher/>` / `mountAskLauncher`. All values are
 * public: `anonUserId` is a hashed, analytics-only id — never a credential.
 */
export interface LauncherConfig {
  /**
   * The iframe route URL (the served widget bundle, e.g.
   * `https://host/support-bot/widget` or a same-origin relative `/support-bot/widget`).
   * The launcher appends a `?host=<encoded origin>` param so the widget can pin
   * the allowed host origin for the handshake.
   */
  widgetUrl: string;
  /** Which edge the panel docks to. Defaults to `'right'`. */
  placement?: 'right' | 'left';
  /** Resolved tenant subdomain — passed to the widget via the AUTH handshake. */
  tenant: string;
  /** Requested locale — passed to the widget via AUTH (normalized widget-side). */
  locale?: string;
  /** Hashed, analytics-only user id — passed to the widget via AUTH. */
  anonUserId?: string;
  /** Stacking context for the launcher button + iframe wrapper. */
  zIndex?: number;
  /**
   * Optional per-tenant display name for the panel header ("Ask {brandName}",
   * e.g. "Ask SWMB"). Passed to the widget via AUTH. When omitted, it falls back
   * to the tenant key upper-cased. Keep it configurable — never hardcode a brand.
   */
  brandName?: string;
  /**
   * Open the panel immediately on mount — for a host deep-link (e.g. the support
   * wrapper page's `?open=1`, where the visitor already clicked "Ask" upstream).
   * Defaults to false (the launcher starts as just the button).
   */
  defaultOpen?: boolean;
  /**
   * Host-navigation handler (the support wrapper). When provided, the widget is
   * told (via AUTH `hostNav`) to post `OPEN_LINK` for in-answer link clicks
   * instead of opening a new tab, and the launcher calls this with the URL so the
   * host can navigate its background surface (e.g. point the wrapped KB iframe at
   * it). Omit on plain host surfaces — links then open normally.
   */
  onHostNavigate?: (url: string) => void;
}

/**
 * The resolved launcher config: every optional field filled with its default,
 * plus the DERIVED `allowedWidgetOrigin` computed from `widgetUrl` via
 * `deriveOrigin`. When `widgetUrl` is a same-origin RELATIVE path, `deriveOrigin`
 * cannot parse it and returns `''`; the launcher then resolves the origin at
 * runtime against `window.location.origin` (which it must do anyway to build the
 * absolute iframe src). The pinned origin is NEVER `'*'`.
 */
export interface ResolvedLauncherConfig {
  widgetUrl: string;
  placement: 'right' | 'left';
  tenant: string;
  locale?: string;
  anonUserId?: string;
  zIndex: number;
  /** Panel-header display name ("Ask {brandName}"); defaults to `tenant.toUpperCase()`. */
  brandName: string;
  /** Whether to open the panel on mount (host deep-link). Defaults to false. */
  defaultOpen: boolean;
  /** True when a host-navigation handler is present (sent to the widget as AUTH `hostNav`). */
  hostNav: boolean;
  /** Host-navigation handler; called with a URL when the widget posts `OPEN_LINK`. */
  onHostNavigate?: (url: string) => void;
  /** Exact origin the launcher accepts widget messages from / posts AUTH to. */
  allowedWidgetOrigin: string;
}

/**
 * Resolve a host-injected `LauncherConfig` into a frozen `ResolvedLauncherConfig`
 * (mirrors the `resolveWidgetConfig` `Object.freeze` pattern). The derived
 * `allowedWidgetOrigin` is `deriveOrigin(widgetUrl)` — an exact origin, never a
 * wildcard.
 */
export function resolveLauncherConfig(overrides: LauncherConfig): Readonly<ResolvedLauncherConfig> {
  return Object.freeze({
    widgetUrl: overrides.widgetUrl,
    placement: overrides.placement ?? DEFAULT_LAUNCHER_PLACEMENT,
    tenant: overrides.tenant,
    ...(overrides.locale !== undefined ? { locale: overrides.locale } : {}),
    ...(overrides.anonUserId !== undefined ? { anonUserId: overrides.anonUserId } : {}),
    zIndex: overrides.zIndex ?? DEFAULT_LAUNCHER_Z_INDEX,
    brandName:
      overrides.brandName !== undefined && overrides.brandName.length > 0
        ? overrides.brandName
        : overrides.tenant.toUpperCase(),
    defaultOpen: overrides.defaultOpen ?? false,
    hostNav: overrides.onHostNavigate !== undefined,
    ...(overrides.onHostNavigate !== undefined ? { onHostNavigate: overrides.onHostNavigate } : {}),
    allowedWidgetOrigin: deriveOrigin(overrides.widgetUrl),
  });
}
